import React, { useState } from 'react'
import { useAuth } from '../lib/hooks'

interface Props {
  changeToken: string
  onSession: () => void
  onCancel: () => void
}

/**
 * Force-Change-Screen — wird gerendert wenn das Passwort älter als 365 Tage ist.
 * User muss neues Passwort setzen, das die Policy erfüllt; Backend validiert
 * via Zod-Schema (passwordSchema) — fängt Verstöße als 400er ein, das hier
 * im Form gerendert wird.
 */
export function ForcePasswordChange({ changeToken, onSession, onCancel }: Props) {
  const { forcePasswordChange } = useAuth()
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError('')
    if (newPassword !== confirm) {
      setError('Passwörter stimmen nicht überein')
      return
    }
    setSubmitting(true)
    try {
      await forcePasswordChange(changeToken, newPassword)
      onSession()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Password change failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: 380 }}>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'var(--ink)',
          margin: '0 0 6px',
        }}
      >
        Passwort erneuern
      </h1>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 16px' }}>
        Dein Passwort ist älter als 365 Tage. Bitte setze ein neues, bevor
        wir dich einloggen.
      </p>
      <p
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          background: 'color-mix(in srgb, var(--ink) 4%, transparent)',
          padding: '8px 10px',
          borderRadius: 6,
          marginBottom: 24,
          lineHeight: 1.5,
        }}
      >
        ≥12 Zeichen · 1 Großbuchstabe · 1 Kleinbuchstabe · 1 Ziffer · 1 Sonderzeichen
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 18,
            padding: '10px 12px',
            background: 'var(--bad-bg)',
            color: 'var(--bad)',
            border: '1px solid color-mix(in srgb, var(--bad) 30%, transparent)',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <PasswordField
          id="new-password"
          label="Neues Passwort"
          autoComplete="new-password"
          value={newPassword}
          onChange={setNewPassword}
          disabled={submitting}
          autoFocus
        />
        <PasswordField
          id="confirm-password"
          label="Wiederholen"
          autoComplete="new-password"
          value={confirm}
          onChange={setConfirm}
          disabled={submitting}
        />

        <button
          type="submit"
          disabled={submitting || !newPassword || !confirm}
          className="sk-btn primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '11px 16px',
            fontSize: 14,
            marginTop: 8,
          }}
        >
          {submitting ? 'Speichern…' : 'Passwort setzen'}
        </button>
      </form>

      <button
        type="button"
        onClick={onCancel}
        disabled={submitting}
        style={{
          marginTop: 18,
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-3)',
          fontSize: 12,
          cursor: submitting ? 'not-allowed' : 'pointer',
          textDecoration: 'underline',
        }}
      >
        Abbrechen
      </button>
    </div>
  )
}

interface PasswordFieldProps {
  id: string
  label: string
  autoComplete: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

function PasswordField({
  id,
  label,
  autoComplete,
  value,
  onChange,
  disabled,
  autoFocus,
}: PasswordFieldProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--ink-2)',
          marginBottom: 6,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </label>
      <input
        id={id}
        name={id}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        required
        minLength={12}
        className="sk-input"
        style={{
          width: '100%',
          fontSize: 14,
          padding: '9px 12px',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </div>
  )
}
